/**
 * Rows for the cart's two distinct kinds of content, which look similar but are
 * semantically different things:
 *
 * - `CartItem` — a product in the cart. Rendered inside an `<li>`, since the
 *   cart's contents are a list.
 * - `SummaryRow` — one Subtotal / Shipping / Taxes / Total figure. Rendered as a
 *   `<dt>`/`<dd>` pair inside a `<dl>`, because those are literally name-value
 *   pairs, and a description list is the element for that.
 *
 * Both share the money formatting in `renderValue` below.
 */
import React from 'react';
import { Money } from './elements/Money.js';
import { Checkmark } from './theme/icons.js';
import styled from './theme/styled.js';
import { token } from './theme/tokens.js';
import { isNil } from './utils/fp-utils.js';

const SummaryCallout = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: token(theme, 'space.tinyGap'),
    padding: token(theme, 'space.smallGap'),
    borderRadius: token(theme, 'radius.description'),
    backgroundColor: token(theme, 'color.highlightBackground'),
    color: token(theme, 'color.highlightText'),
    fontSize: token(theme, 'fontSize.small'),
}));

const PricingSummary = styled.div(({ theme }) => ({
    width: '100%',
    backgroundColor: token(theme, 'color.surfaceSubtle'),
    borderTop: `solid 1px ${token(theme, 'color.borderFooter')}`,

    margin: `0 calc(-1 * clamp(${token(theme, 'space.panelPaddingHorizontal')}, 5%, 5px))`, // Let child element span full parent width to apply backgound/border
    padding: `${token(theme, 'space.mediumGap')} clamp(${token(theme, 'space.panelPaddingHorizontal')}, 5%, 5px)`, // Reset parent padding after margin change
}));

/**
 * A `<dl>`'s content model allows `<div>` wrappers around `dt`/`dd` pairs, which is
 * what makes it possible to lay each pair out as its own flex row while keeping the
 * list valid.
 */
const SummaryGroup = styled.div(({ theme, hasDivider }) => ({
    fontSize: token(theme, 'fontSize.body'),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    // The rule above the Total row. Previously a separate divider element, but a
    // border on the row it divides is both fewer nodes and valid inside a `<dl>`,
    // where a bare `<hr>` between groups is not.
    ...hasDivider ? {
        borderTop: `1px solid ${token(theme, 'color.borderSoft')}`,
        paddingTop: token(theme, 'space.mediumGap'),
    } : {},
}));

// `all: revert` restores the UA stylesheet's `margin-inline-start: 40px` on `<dd>`,
// so both of these need their box explicitly flattened.
const SummaryTerm = styled.dt(({ theme, styles }) => ({
    margin: 0,
    fontFamily: styles.isBold ? token(theme, 'font.bold') : token(theme, 'font.body'),
    fontWeight: styles.isBold ? 'bold' : 'normal',
    ... styles.color ? {
        color: styles.color,
    } : {},
}));

const SummaryValue = styled.dd(({ theme, styles }) => ({
    margin: 0,
    fontSize: '1.15em',
    fontFamily: styles.isBold ? token(theme, 'font.bold') : token(theme, 'font.body'),
    fontWeight: styles.isBold ? 'bold' : 'normal',
    ... styles.color ? {
        color: styles.color,
    } : {},
}));

const ShippingValue = styled.span(({ theme }) => ({
    fontFamily: token(theme, 'font.bold'),
    color: token(theme, 'color.highlightForeground'),
}));

const SmallPrint = styled.div(({ theme }) => ({
    fontSize: token(theme, 'fontSize.small'),
}));

const SummaryRow = ({
    description,
    value,
    styles = { isBold: false },
    hasDivider = false,
}) =>
    <SummaryGroup hasDivider={hasDivider}>
        <SummaryTerm styles={styles}>{description}</SummaryTerm>
        <SummaryValue styles={styles}>{value}</SummaryValue>
    </SummaryGroup>
;

const buildMonthlyPlanSummary = (hasValidPricing, summary) => {
    const breakdowns = summary.subscriptions?.breakdowns?.map((breakdown, index) => {
        const effectivePrice = breakdown.quantity >= breakdown.threshold ? breakdown.discountPrice : breakdown.unitPrice;
        // TODO: make inline list more flexible?
        return <React.Fragment key={index}>
            {index > 0 && <>, </>}
            <Money
                hasValidPricing={hasValidPricing}
                currencySymbol={summary.currencySymbol}
                currencyCode={summary.currencyCode}
                value={effectivePrice}
            />{' × '}{breakdown.quantity}
        </React.Fragment>;
    });

    return <>then <Money
        hasValidPricing={hasValidPricing}
        currencySymbol={summary.currencySymbol}
        currencyCode={summary.currencyCode}
        value={summary.subscriptions.billingAmount}
    />/{summary.subscriptions?.interval} ({breakdowns}) — cancel anytime</>;
};

/**
 * Subtotal / Shipping / Taxes / Total are name-value pairs, which is what a
 * description list is for. See `SummaryRow` for the `<div>`-wrapped `dt`/`dd`
 * grouping this expects as children.
 *
 * `aria-live="polite"` because changing a quantity silently rewrites these
 * figures; without it the only feedback a screen reader user gets is that the
 * stepper's value changed, not what it cost them.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/dl
 */
const SummaryWrapper = styled.dl(({ theme }) => ({
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: token(theme, 'space.smallGap'),
}));

const DefaultSummary = ({
    hasValidPricing,
    summary,
}) => {
    const summaryRows = [
        { id: 'subtotal', label: 'Due today', value: summary.subtotal, isBold: true, showCurrencyCode: true },
        { id: 'shipping', label: 'Shipping', placeholder: <ShippingValue>Free</ShippingValue> },
        { id: 'taxes', label: 'Taxes', value: summary.taxes, placeholder: <SmallPrint>Calculated at checkout</SmallPrint> },
    ];

    if (summary.subscriptions) {
        summaryRows.push(...[
            { id: 'subscription', label: 'Monthly plan', hasDivider: true, placeholder: <strong>
                <Money
                    hasValidPricing={hasValidPricing}
                    currencySymbol={summary.currencySymbol}
                    currencyCode={summary.currencyCode}
                    value={summary.subscriptions?.prorationAmount ?? 0}
                />{' '}today</strong> },
            { id: 'susbcription-detail', label: <SmallPrint>{buildMonthlyPlanSummary(hasValidPricing, summary)}</SmallPrint>, placeholder: '' },
        ]);
    }

    /*
    * Rows come from `useCartDisplayItems` as data, so this is a map rather
    * than four near-identical hand-written blocks. A row's `placeholder`
    * is the copy to show when it has no figure yet — the hook returns the
    * string, this decides it renders as small print.
    */
    return (
        <PricingSummary>
            <CartSummary.Wrapper aria-live="polite">
                {summary.callouts?.map((callout) =>
                    <SummaryCallout key={callout.productCode}>
                        <div style={{ width: 'fit-content' }}><Checkmark /></div>
                        <div>
                            {callout.threshold}+ devices — plan drops to <Money
                                hasValidPricing={hasValidPricing}
                                currencySymbol={summary.currencySymbol}
                                currencyCode={summary.currencyCode}
                                value={callout.discountPrice}
                            />/{callout.interval} each. You save <Money
                                hasValidPricing={hasValidPricing}
                                currencySymbol={summary.currencySymbol}
                                currencyCode={summary.currencyCode}
                                value={callout.savingAmount}
                            />/{callout.interval}.
                        </div>
                    </SummaryCallout>)
                }

                {summaryRows.map(({ id, label, value, placeholder, isBold, hasDivider, showCurrencyCode }) =>
                    <CartSummary.Item
                        key={id}
                        description={label}
                        value={isNil(value) && placeholder ?
                            placeholder :
                            <Money
                                hasValidPricing={hasValidPricing}
                                currencySymbol={summary.currencySymbol}
                                currencyCode={summary.currencyCode}
                                showCurrencyCode={showCurrencyCode}
                                value={value}
                            />
                        }
                        styles={{
                            isBold,
                        }}
                        hasDivider={hasDivider}
                    />,
                )}
            </CartSummary.Wrapper>
        </PricingSummary>
    );
};

const CartSummary = ({ children, ...props }) => children || <DefaultSummary {...props} />;

// Compound component pattern
CartSummary.Wrapper = SummaryWrapper;
CartSummary.Item = SummaryRow;

export default CartSummary;
